import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, FileText, Calendar, MessageSquare, Users, Settings, Shield, LogOut, Menu, X, CheckCircle, XCircle, AlertCircle, Home, Image } from 'lucide-react';
import PublicationsDataTable from '../components/member/PublicationsDataTable';

const EspaceMembre = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSection = location.pathname.split('/')[2] || 'dashboard';
  
  // Initialiser l'authentification et l'utilisateur depuis le localStorage immédiatement
  const getInitialAuthState = () => {
    const currentUser = localStorage.getItem('currentUser');
    const authToken = localStorage.getItem('authToken');
    const memberToken = localStorage.getItem('memberToken');
    
    // Vérifier que les tokens existent ET que l'email est valide
    if (currentUser && (authToken || memberToken)) {
      try {
        const userData = JSON.parse(currentUser);
        // Si l'email est vide, considérer comme non authentifié
        if (!userData.email || userData.email.trim() === '') {
          // Nettoyer les données invalides
          localStorage.removeItem('currentUser');
          localStorage.removeItem('authToken');
          localStorage.removeItem('memberToken');
          localStorage.removeItem('memberEmail');
          localStorage.removeItem('memberFullName');
          return false;
        }
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  };
  
  const getInitialUser = () => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      const authToken = localStorage.getItem('authToken');
      const memberToken = localStorage.getItem('memberToken');
      
      if (currentUser && (authToken || memberToken)) {
        const userData = JSON.parse(currentUser);
        
        // Vérifier que l'email est présent (donnée essentielle)
        if (!userData.email || userData.email.trim() === '') {
          // Données incomplètes - retourner null pour forcer le login
          return null;
        }
        
        const displayName = userData.role === 'admin' ? 'Administrateur (Mode Membre)' : 'Membre';
        return { 
          name: displayName, 
          email: userData.email, 
          role: userData.role,
          isAdminInMemberMode: userData.role === 'admin'
        };
      }
    } catch (e) {
      console.error('Erreur parsing currentUser:', e);
    }
    return null;
  };
  
  const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuthState);
  const [user, setUser] = useState(getInitialUser);
  const [authChecked, setAuthChecked] = useState(getInitialAuthState);  // Si auth initiale, on a déjà vérifié
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null); // Pour les messages de paiement

  // Menu items avec filtre selon le rôle
  const allMenuItems = [
    { id: 'dashboard', icon: User, label: 'Mon profil', description: 'Informations personnelles', roles: ['all'] },
    { id: 'cotisation', icon: Settings, label: 'Ma cotisation', description: 'Statut et paiements', roles: ['membre_annuel', 'membre_vie', 'admin'] },
    { id: 'news', icon: FileText, label: 'News/Blog', description: 'Actualités membres', roles: ['all'] },
    { id: 'events', icon: Calendar, label: 'Événements', description: 'Inscriptions et réservations', roles: ['all'] },
    { id: 'albums', icon: Image, label: 'Albums Photos', description: 'Parcourir les albums', roles: ['all'] },
    { id: 'forum', icon: MessageSquare, label: 'Forum', description: 'Discussions communauté', roles: ['all'] },
    { id: 'publications', icon: FileText, label: 'Publications', description: 'Bulletins et documents', roles: ['all'] },
    { id: 'familles', icon: Shield, label: 'Familles et blasons', description: 'Base généalogique', roles: ['all'] },
    { id: 'contacts', icon: Users, label: 'Contacts', description: 'Coordonnées des membres', roles: ['all'] }
  ];
  
  // Filtrer les items selon le rôle de l'utilisateur
  const menuItems = allMenuItems.filter(item => {
    if (item.roles.includes('all')) return true;
    return item.roles.includes(user?.role);
  });

  useEffect(() => {
    // Vérifier l'authentification au chargement initial uniquement
    // Ne pas rediriger si on navigue entre sections
    if (!user && !authChecked) {
      const currentUser = localStorage.getItem('currentUser');
      const authToken = localStorage.getItem('authToken');
      const memberToken = localStorage.getItem('memberToken');
      
      if (currentUser && (authToken || memberToken)) {
        try {
          const userData = JSON.parse(currentUser);
          
          // Vérifier que les données essentielles sont présentes
          // Si email est vide ou données utilisateur incomplètes, déconnecter
          if (!userData.email || userData.email.trim() === '') {
            console.warn('Données utilisateur incomplètes, redirection vers login');
            localStorage.removeItem('currentUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('memberToken');
            localStorage.removeItem('memberEmail');
            localStorage.removeItem('memberFullName');
            setAuthChecked(true);
            setIsAuthenticated(false);
            return;
          }
          
          setIsAuthenticated(true);
          
          // Définir user immédiatement
          const displayName = userData.role === 'admin' ? 'Administrateur (Mode Membre)' : 'Membre';
          setUser({ 
            name: displayName, 
            email: userData.email, 
            role: userData.role,
            isAdminInMemberMode: userData.role === 'admin'
          });
          setAuthChecked(true);
          
        } catch (e) {
          console.error('Erreur parsing currentUser:', e);
          // En cas d'erreur de parsing, nettoyer et afficher le formulaire de login
          localStorage.removeItem('currentUser');
          localStorage.removeItem('authToken');
          localStorage.removeItem('memberToken');
          localStorage.removeItem('memberEmail');
          localStorage.removeItem('memberFullName');
          setAuthChecked(true);
          setIsAuthenticated(false);
        }
      } else {
        // Pas de données utilisateur - afficher le formulaire de login (pas de redirection)
        setAuthChecked(true);
        setIsAuthenticated(false);
      }
    }
  }, []); // Array vide = s'exécute seulement au montage initial

  // Vérifier les données utilisateur avec l'API au chargement
  useEffect(() => {
    const verifyUserData = async () => {
      if (!isAuthenticated || !authChecked) return;
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      if (!token) return;
      
      try {
        // Utiliser le domaine courant pour éviter les problèmes CORS en production
        const apiBase = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
        
        const response = await fetch(`${apiBase}/api/member/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const profileData = await response.json();
          
          // Vérifier si les données sont invalides
          const isInvalid = (val) => !val || val.trim() === '' || val.trim() === '-' || val === 'undefined';
          
          // Pour les membres extérieurs, on est plus souple (peuvent avoir des noms d'organisation)
          const isMemberExterieur = profileData.role === 'membre_exterieur';
          
          // Pour les membres extérieurs, on vérifie seulement si au moins un des deux champs est rempli
          // Pour les autres membres, on vérifie que les deux sont valides
          if (isMemberExterieur) {
            // Membre extérieur: au moins le nom OU le prénom doit être renseigné
            if (isInvalid(profileData.first_name) && isInvalid(profileData.last_name)) {
              console.warn('⚠️ Données profil membre extérieur invalides, déconnexion forcée');
              localStorage.removeItem('authToken');
              localStorage.removeItem('memberToken');
              localStorage.removeItem('currentUser');
              localStorage.removeItem('memberEmail');
              localStorage.removeItem('memberFullName');
              localStorage.setItem('sessionExpiredMessage', 'Vos données de session sont incomplètes. Veuillez vous reconnecter.');
              window.location.href = '/membre';
              return;
            }
          } else {
            // Membre normal: nom ET prénom doivent être valides
            if (isInvalid(profileData.first_name) || isInvalid(profileData.last_name)) {
              console.warn('⚠️ Données profil invalides, déconnexion forcée');
              localStorage.removeItem('authToken');
              localStorage.removeItem('memberToken');
              localStorage.removeItem('currentUser');
              localStorage.removeItem('memberEmail');
              localStorage.removeItem('memberFullName');
              localStorage.setItem('sessionExpiredMessage', 'Vos données de session sont incomplètes. Veuillez vous reconnecter.');
              window.location.href = '/membre';
              return;
            }
          }
        } else if (response.status === 401 || response.status === 403) {
          // Token invalide
          console.warn('⚠️ Token invalide, déconnexion forcée');
          localStorage.removeItem('authToken');
          localStorage.removeItem('memberToken');
          localStorage.removeItem('currentUser');
          localStorage.setItem('sessionExpiredMessage', 'Votre session a expiré. Veuillez vous reconnecter.');
          window.location.href = '/membre';
        }
      } catch (error) {
        console.error('Erreur vérification profil:', error);
      }
    };
    
    verifyUserData();
  }, [isAuthenticated, authChecked]);

  useEffect(() => {
    // Vérifier les paramètres de paiement dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const year = urlParams.get('year');
    const message = urlParams.get('message');

    if (paymentStatus === 'success') {
      setPaymentMessage({
        type: 'success',
        title: '✅ Paiement réussi !',
        description: year 
          ? `Votre cotisation pour l'année ${year} a été payée avec succès. Merci pour votre contribution !`
          : 'Votre paiement a été effectué avec succès. Merci !'
      });
      // Rediriger vers la section cotisation
      navigate('/membre/cotisation');
      // Nettoyer l'URL après 5 secondes
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setPaymentMessage(null);
      }, 8000);
    } else if (paymentStatus === 'cancelled') {
      setPaymentMessage({
        type: 'error',
        title: '❌ Paiement annulé',
        description: 'Votre paiement a été annulé. Vous pouvez réessayer à tout moment depuis votre espace cotisation.'
      });
      navigate('/membre/cotisation');
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setPaymentMessage(null);
      }, 8000);
    } else if (message === 'already_paid') {
      setPaymentMessage({
        type: 'info',
        title: 'ℹ️ Déjà payé',
        description: 'Cette cotisation a déjà été payée. Merci !'
      });
      navigate('/membre/cotisation');
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setPaymentMessage(null);
      }, 8000);
    }
  }, []);

  const handleLogin = async (email, password) => {
    try {
      // Normalisation anti-espace/casse
      const normalize = (s) => (s || '').replace(/\u00A0/g, ' ').trim();
      const normalizedEmail = normalize(email).toLowerCase();
      const normalizedPassword = normalize(password);
      
      // Utiliser le domaine courant pour les appels d'authentification
      const apiBase = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

      console.log('🔐 Tentative de connexion:', { email: normalizedEmail, backend: apiBase });

      const response = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
      });

      console.log('📡 Réponse du serveur:', { status: response.status, ok: response.ok, statusText: response.statusText });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Données reçues:', { user_role: data.user_role, has_token: !!data.access_token });
        if (['membre_vie', 'membre_annuel', 'membre_exterieur', 'admin'].includes(data.user_role)) {
          
          // Récupérer le profil complet de l'utilisateur (incluant is_moderator)
          const profileResponse = await fetch(`${apiBase}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${data.access_token}`
            }
          });
          
          let userProfile = null;
          if (profileResponse.ok) {
            userProfile = await profileResponse.json();
            
            // Vérifier si les données utilisateur sont valides
            const isInvalidName = (name) => {
              return !name || name.trim() === '' || name.trim() === '-' || name.trim() === 'undefined';
            };
            
            // Pour les membres extérieurs, on est plus souple (au moins un des deux champs doit être valide)
            const isMemberExterieur = data.user_role === 'membre_exterieur';
            
            if (isMemberExterieur) {
              // Membre extérieur: au moins le nom OU le prénom doit être renseigné
              if (isInvalidName(userProfile?.first_name) && isInvalidName(userProfile?.last_name)) {
                console.warn('⚠️ Données utilisateur incomplètes après login (membre extérieur)');
                alert('Vos données de profil sont incomplètes. Veuillez contacter l\'administrateur.');
                return;
              }
            } else {
              // Membre normal: nom ET prénom doivent être valides
              if (isInvalidName(userProfile?.first_name) || isInvalidName(userProfile?.last_name)) {
                console.warn('⚠️ Données utilisateur incomplètes après login');
                alert('Vos données de profil sont incomplètes. Veuillez contacter l\'administrateur.');
                return;
              }
            }
          }
          
          // Utiliser le système unifié
          localStorage.setItem('authToken', data.access_token);
          localStorage.setItem('memberToken', data.access_token); // Compatibilité
          localStorage.setItem('memberEmail', normalizedEmail); // Email pour inscription événements
          // Stocker le nom complet pour pré-remplir les formulaires
          const memberFullName = userProfile?.first_name && userProfile?.last_name 
            ? `${userProfile.first_name} ${userProfile.last_name}`.trim()
            : userProfile?.first_name || userProfile?.last_name || '';
          localStorage.setItem('memberFullName', memberFullName);
          localStorage.setItem('currentUser', JSON.stringify({
            id: userProfile?.id,
            email: normalizedEmail,
            role: data.user_role,
            name: data.user_role === 'admin' ? 'Administrateur (Mode Membre)' : 'Membre',
            first_name: userProfile?.first_name || '',
            last_name: userProfile?.last_name || '',
            token: data.access_token,
            is_moderator: userProfile?.is_moderator || false
          }));
          
          setIsAuthenticated(true);
          
          // Nom affiché selon le rôle
          const displayName = data.user_role === 'admin' ? 'Administrateur (Mode Membre)' : 'Membre';
          setUser({ 
            name: displayName, 
            email: normalizedEmail, 
            role: data.user_role,
            isAdminInMemberMode: data.user_role === 'admin',
            is_moderator: userProfile?.is_moderator || false
          });
        } else {
          console.error('❌ Accès non autorisé - rôle:', data.user_role);
          alert('Accès non autorisé. Seuls les membres peuvent accéder à cette section.');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Échec de connexion:', { status: response.status, error: errorText });
        alert('Identifiants incorrects');
      }
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      alert('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    // Nettoyer tous les tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('memberToken');
    localStorage.removeItem('currentUser');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/membre');
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  // Afficher un loader pendant le chargement des données utilisateur
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'rgb(196, 150, 96)' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white shadow-md p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="w-8 h-8 object-contain"
          />
          <h1 className="text-lg font-bold whitespace-nowrap" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Espace Membres
          </h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar Desktop */}
      <div className="hidden md:flex md:w-64 bg-white shadow-lg flex-shrink-0 relative flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <img 
              src="/images/lignages-logo-admin.png" 
              alt="Logo Lignages" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold whitespace-nowrap" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                Espace Membres
              </h1>
              <p className="text-sm text-gray-600">Lignages de Bruxelles</p>
            </div>
          </div>
          
          {/* Bouton Retour au site - En haut du menu */}
          <a 
            href="/" 
            className="mt-4 flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            style={{ fontFamily: '"Lora", serif' }}
          >
            <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
            <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
              Retour au site
            </span>
          </a>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(`/membre/${item.id}`)}
                className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                  currentSection === item.id ? 'bg-gray-100' : ''
                }`}
                style={{ 
                  fontFamily: '"Lora", serif',
                  borderLeft: currentSection === item.id ? '3px solid rgb(196, 150, 96)' : '3px solid transparent'
                }}
              >
                <div className="flex items-center space-x-3">
                  <item.icon 
                    className="w-5 h-5" 
                    style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#6b7280' }} 
                  />
                  <div>
                    <div 
                      className="font-medium"
                      style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#374151' }}
                    >
                      {item.label}
                    </div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Bouton Admin si l'utilisateur est admin */}
          {user.role === 'admin' && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <a 
                href="/admin" 
                className="flex items-center space-x-2 text-red-600 hover:text-red-800 transition-colors"
                style={{ fontFamily: '"Lora", serif' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Accéder à l'Administration</span>
              </a>
            </div>
          )}
        </nav>

        <div className="p-4">
          <div className="bg-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate flex items-center">
                  {user.name}
                  {user.role === 'admin' && (
                    <span className="ml-2 px-1 py-0.5 bg-red-100 text-red-600 text-xs rounded">Admin</span>
                  )}
                </p>
                <p className="text-xs text-gray-600 truncate">{user.email}</p>
                <p className="text-xs truncate" style={{ color: 'rgb(196, 150, 96)' }}>
                  {user.role === 'admin' ? 'Mode Membre (Administrateur)' :
                   user.role === 'membre_vie' ? 'Membre à vie' : 
                   user.role === 'membre_annuel' ? 'Membre annuel' :
                   user.role === 'membre_exterieur' ? 'Membre extérieur' :
                   user.role === 'decede' ? 'Décédé' :
                   user.role === 'prospect' ? 'Prospect' :
                   user.role === 'non_actif' ? 'Non-actif' :
                   'Membre'}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="hover:bg-gray-200 ml-2 flex-shrink-0"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="w-64 bg-white h-full shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img 
                  src="/images/lignages-logo-admin.png" 
                  alt="Logo Lignages" 
                  className="w-8 h-8 object-contain"
                />
                <h1 className="text-lg font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                  Espace Membres
                </h1>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Bouton Retour au site - En haut du menu mobile */}
            <div className="px-4 pt-3">
              <a 
                href="/" 
                className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                style={{ fontFamily: '"Lora", serif' }}
              >
                <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
                <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
                  Retour au site
                </span>
              </a>
            </div>

            <nav className="p-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(`/membre/${item.id}`);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                      currentSection === item.id ? 'bg-gray-100' : ''
                    }`}
                    style={{ 
                      fontFamily: '"Lora", serif',
                      borderLeft: currentSection === item.id ? '3px solid rgb(196, 150, 96)' : '3px solid transparent'
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon 
                        className="w-5 h-5" 
                        style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#6b7280' }} 
                      />
                      <div>
                        <div 
                          className="font-medium text-sm"
                          style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#374151' }}
                        >
                          {item.label}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Bouton Admin si l'utilisateur est admin */}
              {user.role === 'admin' && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                  <a 
                    href="/admin" 
                    className="flex items-center space-x-2 text-red-600 hover:text-red-800 transition-colors"
                    style={{ fontFamily: '"Lora", serif' }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">Accéder à l'Administration</span>
                  </a>
                </div>
              )}
            </nav>

            <div className="p-4 border-t">
              <div className="bg-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate flex items-center">
                      {user.name}
                      {user.role === 'admin' && (
                        <span className="ml-2 px-1 py-0.5 bg-red-100 text-red-600 text-xs rounded">Admin</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600 truncate">{user.email}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout}
                    className="hover:bg-gray-200 ml-2 flex-shrink-0"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8 w-full">
        {/* Message de confirmation/erreur de paiement */}
        {paymentMessage && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            paymentMessage.type === 'success' 
              ? 'bg-green-50 border-green-500' 
              : paymentMessage.type === 'error'
              ? 'bg-red-50 border-red-500'
              : 'bg-blue-50 border-blue-500'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {paymentMessage.type === 'success' && (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
                {paymentMessage.type === 'error' && (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
                {paymentMessage.type === 'info' && (
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className={`text-lg font-semibold ${
                  paymentMessage.type === 'success' 
                    ? 'text-green-800' 
                    : paymentMessage.type === 'error'
                    ? 'text-red-800'
                    : 'text-blue-800'
                }`}>
                  {paymentMessage.title}
                </h3>
                <p className={`mt-1 text-sm ${
                  paymentMessage.type === 'success' 
                    ? 'text-green-700' 
                    : paymentMessage.type === 'error'
                    ? 'text-red-700'
                    : 'text-blue-700'
                }`}>
                  {paymentMessage.description}
                </p>
              </div>
              <button
                onClick={() => setPaymentMessage(null)}
                className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {currentSection === 'dashboard' && <ProfileSection user={user} />}
        {currentSection === 'cotisation' && <CotisationSection user={user} />}
        {currentSection === 'contacts' && <ContactsSection />}
        {currentSection === 'news' && <NewsSection />}
        {currentSection === 'events' && <EventsSection />}
        {currentSection === 'albums' && <AlbumsPhotosSection />}
        {currentSection === 'forum' && <ForumSection />}
        {currentSection === 'publications' && <PublicationsSection />}
        {currentSection === 'familles' && <FamillesSection />}
      </div>
    </div>
  );
};

// Composant de connexion (réutilisé de l'admin avec adaptations)
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null);

  // Vérifier si un message de session expirée existe
  useEffect(() => {
    const expiredMsg = localStorage.getItem('sessionExpiredMessage');
    if (expiredMsg) {
      setSessionExpiredMsg(expiredMsg);
      // Nettoyer le message du localStorage après l'avoir récupéré
      localStorage.removeItem('sessionExpiredMessage');
      // Faire disparaître le message après 10 secondes
      setTimeout(() => setSessionExpiredMsg(null), 10000);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (useMagicLink) {
      handleMagicLinkRequest();
    } else {
      onLogin(email, password);
    }
  };

  const handleMagicLinkRequest = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      // Utiliser le domaine courant pour l'appel API
      const apiBase = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiBase}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          frontend_url: window.location.origin  // Envoyer le domaine actuel du frontend
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors de l\'envoi du lien' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 relative">
      {/* Bouton Retour au site en haut à gauche */}
      <button
        onClick={() => window.location.href = '/'}
        className="absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all hover:bg-gray-50 shadow-md"
        style={{ 
          borderColor: 'rgb(196, 150, 96)',
          fontFamily: '"Lora", serif'
        }}
      >
        <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
        <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
          Retour au site
        </span>
      </button>

      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="mx-auto h-20 w-20 object-contain"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
            Espace Membres
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Lignages de Bruxelles
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Connexion
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Message de session expirée */}
            {sessionExpiredMsg && (
              <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">Session expirée</p>
                  <p className="text-amber-700 text-sm">{sessionExpiredMsg}</p>
                </div>
              </div>
            )}
            
            {message && (
              <div className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {message.text}
              </div>
            )}
            
            <div className="mb-6 flex gap-2 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setUseMagicLink(false)}
                className={`px-4 py-2 font-medium transition-colors ${
                  !useMagicLink 
                    ? 'border-b-2 border-[rgb(196,150,96)] text-[rgb(196,150,96)]' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Avec mot de passe
              </button>
              <button
                type="button"
                onClick={() => setUseMagicLink(true)}
                className={`px-4 py-2 font-medium transition-colors ${
                  useMagicLink 
                    ? 'border-b-2 border-[rgb(196,150,96)] text-[rgb(196,150,96)]' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sans mot de passe
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent"
                />
              </div>
              
              {!useMagicLink && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Mot de passe
                  </label>
                  <input
                    id="password"
                    type="password"
                    required={!useMagicLink}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  />
                  {/* Lien mot de passe oublié */}
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => window.location.href = '/reset-password'}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>
              )}

              {useMagicLink && (
                <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                  <p>Un lien de connexion sera envoyé à votre adresse email. Ce lien est valide pendant 12 heures.</p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full text-white"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                disabled={loading}
              >
                {loading ? 'Envoi en cours...' : useMagicLink ? 'Recevoir le lien de connexion' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Import du composant de gestion de profil
import ProfileManagementComponent from '../components/member/ProfileManagement';

// Sections de l'espace membre
const ProfileSection = () => <ProfileManagementComponent />;

// Import du composant de gestion des cotisations
import CotisationManagementComponent from '../components/member/CotisationManagement';

const CotisationSection = () => <CotisationManagementComponent />;

// Import du composant Contacts
import MemberContactsComponent from '../components/member/MemberContacts';

const ContactsSection = () => <MemberContactsComponent />;

// Import du composant Albums Photos
import AlbumsPhotosComponent from '../components/member/AlbumsPhotos';

const AlbumsPhotosSection = () => <AlbumsPhotosComponent />;

// Import du composant Forum
import ForumManagementComponent from '../components/ForumManagement';

const ForumSection = () => {
  const location = useLocation();
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  // Extraire le topicId depuis l'URL si présent
  // URL: /membre/forum/topic/:topicId
  const pathParts = location.pathname.split('/');
  const topicId = pathParts[3] === 'topic' && pathParts[4] ? pathParts[4] : null;
  
  return <ForumManagementComponent 
    userRole={currentUser.role} 
    isAdmin={currentUser.role === 'admin'} 
    isModerator={currentUser.is_moderator || false}
    isAdminSpace={false}
    initialTopicId={topicId}
  />;
};

// Autres sections placeholder
const NewsSection = () => {
  const NewsManagement = require('../components/member/NewsManagement').default;
  return <NewsManagement />;
};

const EventsSection = () => {
  const EventsManagement = require('../components/member/EventsManagement').default;
  return <EventsManagement />;
};

const PublicationsSection = () => {
  return <PublicationsDataTableWrapper />;
};

// Wrapper pour Publications avec filtres
const PublicationsDataTableWrapper = () => {
  const [selectedRubrique, setSelectedRubrique] = useState('all');
  const [rubriques, setRubriques] = useState([]);

  useEffect(() => {
    const loadRubriques = async () => {
      try {
        const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/publications/rubriques`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setRubriques(data.rubriques || []);
        }
      } catch (error) {
        console.error('Erreur chargement rubriques:', error);
      }
    };
    loadRubriques();
  }, []);
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
          Publications
        </h2>
        <p className="text-gray-600 mb-4">Consultez et téléchargez les publications de l'association</p>
        
        {/* Filtres par rubrique */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedRubrique('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedRubrique === 'all' ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={selectedRubrique === 'all' ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
          >
            Toutes
          </button>
          {rubriques.map((rubrique) => (
            <button
              key={rubrique.id}
              onClick={() => setSelectedRubrique(rubrique.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedRubrique === rubrique.id ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedRubrique === rubrique.id ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
            >
              {rubrique.name}
            </button>
          ))}
        </div>
      </div>

      <PublicationsDataTable selectedRubrique={selectedRubrique} readOnly={true} />
    </div>
  );
};

const FamillesSection = () => {
  const FamillesMembre = require('../pages/FamillesMembre').default;
  return <FamillesMembre />;
};

export default EspaceMembre;